import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getLenis } from '@/components/motion/smooth-scroll'
import { SplitWords } from '@/components/motion/split-text'
import RollText from '@/components/motion/roll-text'
import Button from '@/components/generals/button'
import LiveClock from '@/components/generals/live-clock'

import {
    navLinks,
    shopCategories,
    siteConfig,
    socialLinks,
    stores
} from '@/constants/site'

const WORDMARK = ['E', 'L', 'I', 'O']

const shopLinks = [
    { label: 'All pieces', href: '/shop' },
    ...shopCategories.slice(0, 3).map(category => ({ label: category.label, href: `/shop?category=${category.slug}` }))
]

const studioLinks = navLinks.filter(link => link.href !== '/shop')

const Footer: FC = (): ReactNode => {
    const backToTop = (): void => {
        const lenis = getLenis()

        if (lenis) lenis.scrollTo(0, { duration: 1.8 })
        else window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    return (
        <footer className='relative overflow-hidden border-t border-line bg-ink text-bone'>
            <div className='grid gap-12 pb-14 pt-20 gutter md:grid-cols-12 md:gap-8 md:pt-28'>
                <div className='md:col-span-5 lg:col-span-4'>
                    <p data-reveal='words' className='max-w-[17ch] serif-m text-bone'>
                        <SplitWords text='Essentials for the unordinary. Made in numbered runs, repaired for life.' />
                    </p>

                    <div data-reveal='fade' data-reveal-delay='0.2' className='mt-8'>
                        <Button href='/#the-list' variant='ghost'>
                            Join the list
                        </Button>
                    </div>
                </div>

                <nav aria-label='Shop' className='md:col-span-2 lg:col-start-6'>
                    <p className='meta-mono text-ash'>
                        (Shop)
                    </p>

                    <ul className='mt-5 space-y-2.5'>
                        {shopLinks.map(link => (
                            <li key={link.href}>
                                <Link href={link.href} className='roll-host body-copy text-bone/70 transition-colors duration-300 hover:text-bone'>
                                    <RollText text={link.label} />
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <nav aria-label='Studio' className='md:col-span-2'>
                    <p className='meta-mono text-ash'>
                        (Studio)
                    </p>

                    <ul className='mt-5 space-y-2.5'>
                        {studioLinks.map(link => (
                            <li key={link.href}>
                                <Link href={link.href} className='roll-host body-copy text-bone/70 transition-colors duration-300 hover:text-bone'>
                                    <RollText text={link.label} />
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='md:col-span-3 md:col-start-10'>
                    <p className='meta-mono text-ash'>
                        (Stores)
                    </p>

                    <ul className='mt-5'>
                        {stores.map(store => (
                            <li key={store.city} className='flex items-center justify-between border-b border-line py-3'>
                                <Link href='/contact#stores' className='body-copy text-bone transition-colors duration-300 hover:text-acid'>
                                    {store.city}
                                </Link>

                                <span className='flex items-center gap-2 meta-mono text-ash'>
                                    <span className='ping' aria-hidden='true' />

                                    <LiveClock timeZone={store.timeZone} seconds={false} />
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div
                data-reveal='chars'
                className='flex select-none justify-center font-heading text-[34vw] leading-[0.78] tracking-[-0.02em] text-bone md:text-[24vw]'
            >
                <span className='sr-only'>
                    {siteConfig.name}
                </span>

                {WORDMARK.map(letter => (
                    <span key={letter} aria-hidden='true' className='mask'>
                        <span className='transition-[color,transform] duration-700 ease-expo-out hover:-translate-y-[4%] hover:text-acid'>
                            {letter}
                        </span>
                    </span>
                ))}
            </div>

            <div className='mt-8 flex flex-col gap-5 border-t border-line py-6 gutter md:flex-row md:items-center md:justify-between'>
                <p className='meta-mono text-ash'>
                    © 2026 {siteConfig.legalName} — Berlin
                </p>

                <ul className='flex flex-wrap gap-x-6 gap-y-2'>
                    {socialLinks.map(link => (
                        <li key={link.label}>
                            <a
                                href={link.href}
                                target='_blank'
                                rel='noreferrer'
                                className='roll-host meta-mono text-bone/70 transition-colors duration-300 hover:text-acid'
                            >
                                <RollText text={link.label} />
                            </a>
                        </li>
                    ))}
                </ul>

                <button
                    type='button'
                    onClick={backToTop}
                    className={cn('roll-host group flex items-center gap-2 label-mono text-bone transition-colors duration-300 hover:text-acid')}
                >
                    <RollText text='Designed for the unordinary' />

                    <ArrowUp aria-hidden='true' className='size-4 transition-transform duration-500 ease-expo-out group-hover:-translate-y-1' />

                    <span className='sr-only'>
                        Back to top
                    </span>
                </button>
            </div>
        </footer>
    )
}

export default Footer
